import './tools.css';
import type { RisClient } from '../../client';
import type { ViewerShell } from '../../../shell';
import { AssistantApi } from '../api';
import type { MessageHandlers } from '../messages';
import type { AssistantPanel } from '../panel';
import { ToolsApi } from './api';
import { CommandButton, runCommand } from './command';
import type { ToolDeps } from './dom';
import { LESION_TEXT, showLesions } from './lesions';
import { install as installMarks, marksDrawnOn } from './marks';
import { MEASURE_TEXT, measureWithAi } from './measure';
import { cancelPick } from './picker';
import { askRegion, REGION_TEXT } from './region';

/**
 * The Asistente tools (viewer-ai round 2 §5 VIEW-B): "Preguntar por área", "Medir con IA", "Lesiones"
 * in the panel's tools row, and "Comando" beside the microphone. Registered by one line in
 * ../index.ts; each tool lives in its own module here.
 */

interface ToolsDebug {
  marks(cell?: number): number;
  command(text: string): Promise<void>;
}

declare global {
  interface Window {
    __viewerAssistantTools?: ToolsDebug;
  }
}

/** Hidden tab: stop a pick or a recording in progress (the panel does the same for dictation). */
function onHidden(panel: AssistantPanel, stop: () => void): void {
  const host = panel.root.parentElement ?? panel.root;
  new MutationObserver(() => {
    if (host.hidden || panel.root.hidden) stop();
  }).observe(host, { attributes: true, attributeFilter: ['hidden'] });
}

function placeCommand(panel: AssistantPanel, command: CommandButton): void {
  if (!command.element) return;
  const mic = panel.root.querySelector('.assistant-mic');
  if (mic) mic.after(command.element);
  else panel.root.querySelector('.assistant-form-row')?.prepend(command.element);
}

export function installTools(shell: ViewerShell, panel: AssistantPanel, client: RisClient, handlers: MessageHandlers): void {
  const deps: ToolDeps = { shell, panel, api: new ToolsApi(client), assistantApi: new AssistantApi(client), handlers };
  installMarks(shell);
  panel.addToolButton(REGION_TEXT.button, () => void askRegion(deps));
  panel.addToolButton(MEASURE_TEXT.button, () => void measureWithAi(deps));
  panel.addToolButton(LESION_TEXT.button, () => void showLesions(deps));
  const command = new CommandButton(deps);
  placeCommand(panel, command);
  onHidden(panel, () => {
    cancelPick();
    command.cancel();
  });
  window.__viewerAssistantTools = {
    marks: (cell = 0) => marksDrawnOn(cell),
    command: (text) => runCommand(deps, text),
  };
}
